import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Inbox, ArrowRight, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import API from '@/config/api';

const StatusPill = ({ status, count }) => {
  if (count === 0) return null;
  const meta = {
    pending: { label: 'Pending', className: 'bg-slate-100 text-slate-700 border-slate-200' },
    under_review: { label: 'Follow up', className: 'bg-amber-100 text-amber-800 border-amber-200' },
    approved: { label: 'Approved', className: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
    rejected: { label: 'Rejected', className: 'bg-red-100 text-red-700 border-red-200' },
    waitlisted: { label: 'Waitlist', className: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  }[status] || { label: status, className: 'bg-slate-100' };
  return (
    <Badge className={`text-xs ${meta.className} border`} data-testid={`app-count-${status}`}>
      {meta.label}: {count}
    </Badge>
  );
};

const ApplicationsWidget = () => {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/applications`, getAuthHeaders());
        if (mounted) setApps(res.data || []);
      } catch {
        // Silent fail — widget hides its details
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
     
  }, []);

  if (loading) return null;

  const counts = apps.reduce((acc, a) => {
    acc[a.status || 'pending'] = (acc[a.status || 'pending'] || 0) + 1;
    return acc;
  }, {});

  const pending = counts.pending || 0;
  const followUp = counts.under_review || 0;
  const needsAttention = pending + followUp;

  return (
    <Card className="card-clay" data-testid="applications-widget">
      <CardHeader className="flex flex-row items-center justify-between gap-2 pb-3">
        <CardTitle className="font-heading text-lg flex items-center gap-2">
          <Inbox className="w-5 h-5 text-primary" /> Applications
          {needsAttention > 0 && (
            <Badge className="bg-red-100 text-red-700 border border-red-200 text-xs ml-1" data-testid="applications-needs-attention">
              {needsAttention} waiting
            </Badge>
          )}
        </CardTitle>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/settings?tab=applications')}
          data-testid="applications-review-btn"
        >
          Review <ArrowRight className="w-4 h-4 ml-1" />
        </Button>
      </CardHeader>
      <CardContent>
        {apps.length === 0 ? (
          <p className="text-sm text-muted-foreground">No applications yet — share your application link from Settings.</p>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
              <StatusPill status="pending" count={counts.pending || 0} />
              <StatusPill status="under_review" count={counts.under_review || 0} />
              <StatusPill status="approved" count={counts.approved || 0} />
              <StatusPill status="rejected" count={counts.rejected || 0} />
              <StatusPill status="waitlisted" count={counts.waitlisted || 0} />
            </div>
            {needsAttention > 0 ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <AlertCircle className="w-4 h-4 text-amber-500" />
                {pending > 0 && <><span className="text-foreground font-medium">{pending}</span> new</>}
                {pending > 0 && followUp > 0 && <span>·</span>}
                {followUp > 0 && <><span className="text-foreground font-medium">{followUp}</span> to follow up on</>}
              </div>
            ) : (
              <div className="flex items-center gap-2 text-sm text-emerald-700">
                <CheckCircle className="w-4 h-4" /> Nothing waiting for review — nicely done.
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ApplicationsWidget;
